import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  Box,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
} from '@mui/material'

import { useTransactionsFilters } from '@/views/transactions-player/hooks/use-transactions-filters.hooks'
import { Search, FilterAlt, Delete } from '@mui/icons-material'
import { TransactionsFiltersDrawer } from './transactions-filters-drawer.component'
import { useTransactionsActions } from '@/views/transactions-player/hooks/use-transactions-actions.hooks'
import { useSelector } from 'react-redux'
import { playerTransactionsSelector } from '@/state/player-transactions/slices/index.slices'

export const TransactionsFilters = () => {
  const { t } = useTranslation()

  const { filters, handleChangeSearch, submitSearchFilter } =
    useTransactionsFilters()
  const { getFilterOptions } = useTransactionsActions()
  const { filterOptions } = useSelector(playerTransactionsSelector)

  const statusOptions = filterOptions?.transactionStatus?.map(
    (status: string) => {
      return (
        <MenuItem key={status} value={status}>
          {t(`transaction.transactionStatusOptions.${status}`)}
        </MenuItem>
      )
    },
  )

  const [openDrawer, setOpenDrawer] = React.useState(false)

  React.useEffect(() => {
    if (!filterOptions) {
      getFilterOptions()
    }
  }, [])

  const handleApplyFilter = (newFilters: Partial<any>) => {
    handleChangeSearch(newFilters)
    setTimeout(() => {
      submitSearchFilter({ ...filters, ...newFilters, page: 1 })
    }, 100)
  }

  // const handleDeleteFilter = (key: string, value?: string) => {
  //   if (Array.isArray(filters[key as keyof typeof filters])) {
  //     const currentArray = filters[key as keyof typeof filters] as string[]
  //     const newArray = currentArray.filter((item) => item !== value)
  //     handleApplyFilter({ [key]: newArray.length > 0 ? newArray : undefined })
  //   } else {
  //     handleApplyFilter({ [key]: undefined })
  //   }
  // }

  const activeChips: React.ReactNode[] = []

  const addChip = (key: string, label: string, values: string[]) => {
    if (values.length === 0) return

    const displayValue = values.join(', ')
    activeChips.push(
      <Chip
        key={key}
        label={`${label}: ${displayValue}`}
        onDelete={() => handleApplyFilter({ [key]: undefined })}
        sx={{ margin: 0.5 }}
      />,
    )
  }

  if (filters.transactionType && filters.transactionType.length > 0) {
    const translatedTypes = filters.transactionType.map((type: string) =>
      t(`transaction.transactionTypeOptions.${type}`),
    )
    addChip(
      'transactionType',
      t('transaction.transactionTypeLabel'),
      translatedTypes,
    )
  }

  if (filters.transactionSubType) {
    if (Array.isArray(filters.transactionSubType)) {
      if (filters.transactionSubType.length > 0) {
        addChip(
          'transactionSubType',
          t('transaction.transactionSubType'),
          filters.transactionSubType,
        )
      }
    } else {
      addChip('transactionSubType', t('transaction.transactionSubType'), [
        filters.transactionSubType,
      ])
    }
  }

  if (filters.submittedDate) {
    addChip('submittedDate', t('transaction.submittedDate'), [
      filters.submittedDate,
    ])
  }

  if (filters.completedDate) {
    addChip('completedDate', t('transaction.completedDate'), [
      filters.completedDate,
    ])
  }

  if (filters.walletType) {
    addChip('walletType', t('transaction.walletTypeText'), [filters.walletType])
  }

  if (filters.provider) {
    if (Array.isArray(filters.provider)) {
      if (filters.provider.length > 0) {
        addChip('provider', t('transaction.provider'), filters.provider)
      }
    } else {
      addChip('provider', t('transaction.provider'), [filters.provider])
    }
  }

  if (filters.group) {
    addChip('group', t('transaction.group'), [filters.group])
  }

  if (filters.subGroup) {
    if (Array.isArray(filters.subGroup)) {
      if (filters.subGroup.length > 0) {
        addChip('subGroup', t('transaction.subGroup'), filters.subGroup)
      }
    } else {
      addChip('subGroup', t('transaction.subGroup'), [filters.subGroup])
    }
  }

  // Debounce search for externalTransactionId
  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      submitSearchFilter({
        externalTransactionId: filters.externalTransactionId,
      })
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.externalTransactionId])

  return (
    <>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={4} lg={4}>
          <TextField
            autoComplete="off"
            fullWidth
            name="externalTransactionId"
            label={t('transaction.search')}
            margin="dense"
            variant="outlined"
            id="externalTransactionId"
            value={filters?.externalTransactionId || ''}
            onChange={(event: React.ChangeEvent<any>) =>
              handleChangeSearch({ externalTransactionId: event.target.value })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => submitSearchFilter()}>
                    <Search fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={2}>
          <TextField
            autoComplete="off"
            fullWidth
            name="transactionStatus"
            label={t('transaction.status')}
            id="transactionStatus"
            margin="dense"
            variant="outlined"
            value={filters?.transactionStatus || ''}
            onChange={(event: React.ChangeEvent<any>) =>
              handleApplyFilter({ transactionStatus: event.target.value })
            }
            select
          >
            <MenuItem value="">
              <Box fontStyle="italic">{t('all')}</Box>
            </MenuItem>
            {statusOptions}
          </TextField>
        </Grid>

        <Grid item lg={4} sx={{ display: { xs: 'none', lg: 'block' } }} />

        <Grid
          item
          xs={12}
          md={4}
          lg={2}
          display="flex"
          justifyContent="flex-end"
        >
          <Button
            onClick={() => setOpenDrawer(true)}
            variant="contained"
            color="inherit"
            sx={{
              height: '40px',
              backgroundColor: '#fff',
              color: '#000',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
            startIcon={<FilterAlt />}
          >
            {t('transaction.filters')}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" flexWrap="wrap">
            {activeChips}
            {activeChips.length > 0 && (
              <Button
                color="error"
                onClick={() =>
                  handleApplyFilter({
                    transactionType: undefined,
                    transactionSubType: undefined,
                    submittedDateStart: undefined,
                    submittedDateEnd: undefined,
                    completedDateStart: undefined,
                    completedDateEnd: undefined,
                    walletType: undefined,
                    provider: undefined,
                    group: undefined,
                    subGroup: undefined,
                  })
                }
                sx={{
                  textTransform: 'none',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.lighter',
                  },
                }}
                startIcon={<Delete />}
              >
                {t('labels.clear')}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      <TransactionsFiltersDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onApply={handleApplyFilter}
        currentFilters={filters}
        filterOptions={filterOptions}
      />
    </>
  )
}
